<template>
  <div v-if="isDesktopBrowser"
       v-show="showSectionPointer"
       class="topic-section-pointer wkce-icon-arrow-right"
       :class="[ pointerClass, { 'topic-outline-hidden': !isOutlineVisible }]"
       :style="positionTop" />
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import {
    SET_SECTION_POINTER_VISIBLE,
    SET_SECTION_POINTER_CLASS
} from '_acaSrc/store/topic/topic.store';
import { getDocument, getWindow } from '_acaSrc/utility/DOM';
import { Listener } from '_acaSrc/utility/Events';

const SECTION_POINTER_YPOS_OFFSET_PX = 167;
const RENDERING_TIMEOUT_MS = 300;
const TABS_HEIGHT_RETRY_FRAMES = 8;
const POINTER_ICON_HEIGHT_PX = 20;

export default {
    data() {
        return {
            verticalPosition: 0,
            topicTabsHeightPx: 0,
            topicTabsEl: null,
            topicTabsResizeObserver: null,
            observedTopicTabsEl: null,
            pointerPositionRafId: null
        };
    },
    computed: {
        ...mapGetters('app', [ 'isDesktopBrowser' ]),
        ...mapGetters('topic', [
            'pointerClass',
            'pointerTarget',
            'pointerVisible',
            'pointerPosition',
            'isOutlineVisible',
            'topicLanguage'
        ]),
        ...mapGetters('device', [ 'isDesktopView', 'isMobileOnDesktop' ]),
        positionTop() {
            return `top: ${this.verticalPosition}px`;
        },
        showSectionPointer() {
            return this.isDesktopView && this.topicLanguage !== 'ar';
        }
    },
    watch: {
        pointerPosition(newVal) {
            if (newVal > 0) {
                this.placePointerWithStableTabsHeight(newVal);
            }
            else if (newVal === 0) {
                this.cancelPointerPositionRetry();
                this.verticalPosition = 0;
            }
        },
        pointerVisible(newVal, oldVal) {
            if (!newVal && oldVal) {
                this[SET_SECTION_POINTER_CLASS]('topic-section-pointer-animate-out');
            }
            else {
                this[SET_SECTION_POINTER_CLASS]('topic-section-pointer-animate-in');
            }
        },
        isMobileOnDesktop() {
            this.syncTopicTabsElement();

            if (this.topicTabsEl) {
                setTimeout(() => {
                    const freshHeight = this.topicTabsEl?.offsetHeight ?? 0;
                    if (freshHeight > 0) {
                        this.topicTabsHeightPx = freshHeight;
                        this.updatePointerVerticalPosition(this.pointerPosition);
                    }
                }, RENDERING_TIMEOUT_MS);
            }
        }
    },
    mounted() {
        if (this.isDesktopBrowser) {
            this.$nextTick(() => {
                this.windowResizeListener = new Listener(getWindow(), 'resize',
                    this.onViewportChange, { passive: true });
                this.setListener(this.windowResizeListener);

                this.windowScrollListener = new Listener(getWindow(), 'scroll',
                    this.onViewportChange, { passive: true });
                this.setListener(this.windowScrollListener);

                this[SET_SECTION_POINTER_VISIBLE](false);

                this.syncTopicTabsElement();
                if (this.topicTabsEl) {
                    this.topicTabsHeightPx = this.topicTabsEl.offsetHeight;
                }

                const WindowCtor = getWindow();
                if (WindowCtor.ResizeObserver) {
                    this.topicTabsResizeObserver = new WindowCtor.ResizeObserver(() => {
                        const freshHeight = this.topicTabsEl?.offsetHeight ?? 0;
                        if (freshHeight > 0 && freshHeight !== this.topicTabsHeightPx) {
                            this.topicTabsHeightPx = freshHeight;
                            this.updatePointerVerticalPosition(this.pointerPosition);
                        }
                    });
                    this.syncTopicTabsElement();
                }
            });
        }
    },
    beforeUnmount() {
        if (this.isDesktopBrowser) {
            this.clearListener(this.windowResizeListener);
            this.clearListener(this.windowScrollListener);
        }
        this.cancelPointerPositionRetry();
        if (this.topicTabsResizeObserver && this.observedTopicTabsEl) {
            this.topicTabsResizeObserver.unobserve(this.observedTopicTabsEl);
        }
        if (this.topicTabsResizeObserver) {
            this.topicTabsResizeObserver.disconnect();
        }
    },
    methods: {
        ...mapActions('app', [ 'setListener', 'clearListener' ]),
        ...mapMutations('topic', [
            SET_SECTION_POINTER_VISIBLE,
            SET_SECTION_POINTER_CLASS
        ]),
        onViewportChange() {
            this[SET_SECTION_POINTER_VISIBLE](false);
        },
        syncTopicTabsElement() {
            const nextTopicTabsEl = getDocument().querySelector('#topicContentTabs');
            this.topicTabsEl = nextTopicTabsEl;

            if (this.topicTabsResizeObserver
                && this.observedTopicTabsEl !== nextTopicTabsEl) {
                if (this.observedTopicTabsEl) {
                    this.topicTabsResizeObserver.unobserve(this.observedTopicTabsEl);
                }
                if (nextTopicTabsEl) {
                    this.topicTabsResizeObserver.observe(nextTopicTabsEl);
                }
                this.observedTopicTabsEl = nextTopicTabsEl;
            }

            return nextTopicTabsEl;
        },
        _resolveLineHeight(style) {
            const parsed = parseFloat(style.lineHeight);
            return isNaN(parsed) ? (parseFloat(style.fontSize) || 16) * 1.2 : parsed;
        },
        _computeCenteredOffset(pointerTargetEl, style) {
            const borderTop = parseFloat(style.borderTopWidth) || 0;
            const paddingTop = parseFloat(style.paddingTop) || 0;

            if (borderTop > 0 || paddingTop > 0) {
                const lineHeight = this._resolveLineHeight(style);
                return Math.max(
                    0,
                    Math.round(borderTop + paddingTop + lineHeight / 2 - POINTER_ICON_HEIGHT_PX / 2)
                );
            }

            const targetHeight = pointerTargetEl.offsetHeight ?? 0;
            return Math.max(0, Math.round((targetHeight - POINTER_ICON_HEIGHT_PX) / 2));
        },
        updatePointerVerticalPosition(pointerPos) {
            if (pointerPos <= 0) {
                return;
            }

            const pointerTargetEl = this.pointerTarget;
            if (pointerTargetEl && pointerTargetEl.isConnected) {
                const windowEl = getWindow();
                const currentScroll = windowEl.pageYOffset | windowEl.scrollTop;
                const rect = pointerTargetEl.getBoundingClientRect();
                const targetTop = Math.round(rect.top) + currentScroll;
                const style = windowEl.getComputedStyle(pointerTargetEl);
                const centeredOffset = this._computeCenteredOffset(pointerTargetEl, style);
                this.verticalPosition = targetTop + centeredOffset;
                return;
            }

            this.verticalPosition = pointerPos
                + SECTION_POINTER_YPOS_OFFSET_PX
                + this.topicTabsHeightPx;
        },
        cancelPointerPositionRetry() {
            if (this.pointerPositionRafId) {
                getWindow().cancelAnimationFrame(this.pointerPositionRafId);
                this.pointerPositionRafId = null;
            }
        },
        _shouldRetryTabsHeight(topicTabsEl, freshHeight, frame) {
            return topicTabsEl && freshHeight === 0 && frame < TABS_HEIGHT_RETRY_FRAMES;
        },
        placePointerWithStableTabsHeight(pointerPos, frame = 0) {
            this.cancelPointerPositionRetry();
            const topicTabsEl = this.syncTopicTabsElement();
            const freshHeight = topicTabsEl?.offsetHeight ?? 0;
            const shouldFocusPointer = !this.pointerVisible;

            if (freshHeight > 0) {
                this.topicTabsHeightPx = freshHeight;
            }

            if (this._shouldRetryTabsHeight(topicTabsEl, freshHeight, frame)) {
                this.pointerPositionRafId = getWindow().requestAnimationFrame(() => {
                    this.placePointerWithStableTabsHeight(pointerPos, frame + 1);
                });
                return;
            }

            this.updatePointerVerticalPosition(pointerPos);
            this[SET_SECTION_POINTER_VISIBLE](true);
            if (shouldFocusPointer) {
                this.focusOnPointerElement();
            }
        },
        focusOnPointerElement(){
            if (this.pointerTarget) {
                this.pointerTarget.setAttribute('tabindex', '-1');
                this.$nextTick(() => {
                    this.pointerTarget.focus({ preventScroll: true });
                    this.pointerTarget.blur();
                    this.pointerTarget.removeAttribute('tabindex');
                });
            }
        }
    }
};
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

@SECTION-POINTER-COLOR: @WKCE-GREEN-SHADE5;

@media only screen and (min-width: 768px) {
  .topic-section-pointer {
    position: absolute;
    opacity: 0;
    top: 163px;
    left: 289px;
    width: 20px;
    height: 20px;

    &.topic-outline-hidden {
      left: 32px;
    }

    &.wkce-icon-arrow-right::before {
      color: @SECTION-POINTER-COLOR;
      font-size: 20px;
      font-weight: bold;
    }

    &.topic-section-pointer-animate-in {
      animation-name: wkce-fadein-from-left;
      animation-fill-mode: both;
      animation-duration: 0.2s;
      will-change: transform, opacity;
    }

    &.topic-section-pointer-animate-out {
      animation-name: wkce-fadeout-from-right;
      animation-fill-mode: both;
      animation-duration: 0.2s;
      will-change: transform, opacity;
    }
  }
}

@media only screen and (min-width: 992px) {
  .topic-section-pointer {
    left: 329px;
  }
}

@media only screen and (min-width: 1200px) {
  .topic-section-pointer {
    left: 369px;
  }
}

</style>
